// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
//
// EXTRACTION PLAN -- required by the expert-legibility rule in
// `.claude/rules/anti-ai-slop.md`: this module was past the 400-line threshold.
//
// ALREADY EXTRACTED: the judgment receipts -> `./AskV2ReviewJudgmentReceipt.tsx`. The tag and
// source filter -> `./AskV2ReviewTagFilter.tsx`. Which section a finding sits
// in -> `reviewCommentGroup` in `../../utils/visibleReviewComments.ts`. The
// section heading, toggle and list -> `./AskV2ReviewCommentSection.tsx`. One
// card -> `./AskV2ReviewCommentCard.tsx`; card behaviour belongs there, not here.
// The moved-to-a-section snackbar -> `./useReviewCommentMoveNotice.ts`. The
// header row -> `./AskV2ReviewCommentsHeader.tsx`. Section and band open state
// and keeping the selected card in sight -> `./useReviewCommentSections.ts`.
// Memory and claim citations -> `./useReviewCommentCitations.ts`. To decide
// / All and Group by -> `./useReviewListView.ts`.
import { CheckCircle } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import {
  AskV2ReviewCommentCard,
  type AskV2ReviewCommentDeleteHandler,
  type AskV2ReviewCommentEditHandler,
} from "./AskV2ReviewCommentCard";
import { AskV2ReviewCommentSection } from "./AskV2ReviewCommentSection";
import {
  AskV2ReviewCommentsHeader,
  type AskV2ReviewSourceFilter,
} from "./AskV2ReviewCommentsHeader";
import {
  reviewListEmptyState,
  type ReviewListPinGrouping,
  type ReviewListShowMinorControl,
} from "./AskV2ReviewFilterMenuParts";
import { AskV2ReviewGroupSection } from "./AskV2ReviewGroupSection";
import { type RejectionChoice } from "./AskV2ReviewRejectScopePrompt";
import { AskV2ReviewSeverityBands } from "./AskV2ReviewSeverityBands";
import { groupMark, type PinGroup } from "./pinGroups";
import {
  defaultActiveReviewCommentTags,
  type CommentWithOptionalTags,
  type ReviewCommentTagId,
} from "./reviewCommentTags";
import { useReviewCommentCitations } from "./useReviewCommentCitations";
import {
  openPinGroup,
  useReviewCommentSections,
} from "./useReviewCommentSections";
import {
  type ReviewListStatusFilter,
  riskCounts,
  useReviewListView,
} from "./useReviewListView";

import { type AssetFile } from "../../components/ask-puntt-review";

type AskV2ReviewCommentsListProps = {
  comments: readonly CommentWithOptionalTags[];
  /** A finding's typography companions, drawn inside its card (`pinGroups.attachCompanions`). */
  companionsOf?: ReadonlyMap<string, readonly CommentWithOptionalTags[]>;
  commentNumbers?: Readonly<Record<string, number>>;
  contextFiles?: readonly AssetFile[];
  reviewFiles?: readonly AssetFile[];
  selectedCommentId?: string | null;
  authorNameByUserId?: Readonly<Record<string, string>>;
  currentUserId?: string;
  currentUserName?: string;
  activeTags?: ReadonlySet<ReviewCommentTagId>;
  alwaysOn?: boolean;
  showTagFilter?: boolean;
  onToggleTag?: (tag: ReviewCommentTagId, checked: boolean) => void;
  onToggleAlwaysOn?: (checked: boolean) => void;
  /**
   * Comments before the filter. The section heading counts how many of these
   * the filter hid. Absent, nothing is reported hidden.
   */
  allComments?: readonly CommentWithOptionalTags[];
  /** The "raised by" half of the filter; absent when no expert raised a finding. */
  sourceFilter?: AskV2ReviewSourceFilter;
  judgmentPending?: boolean;
  onJudge?: (
    commentId: string,
    liked: boolean | null,
    rejection?: RejectionChoice,
  ) => void;
  /** Saves the caller's edit of their own comment; absent, no card offers Edit. */
  onEdit?: AskV2ReviewCommentEditHandler;
  /** Soft-deletes the caller's own comment; absent, no card offers Delete. */
  onDelete?: AskV2ReviewCommentDeleteHandler;
  onSelect: (commentId: string) => void;
  onClear: () => void;
  /** Drawn right after the Unresolved section: what an earlier version raised and this one fixed. */
  afterUnresolved?: ReactNode;
  /** The open findings in groups (spec dave.savostyanov/0090); absent, Unresolved lists them flat. */
  pinGroups?: {
    groups: readonly PinGroup[];
    openGroupKey: string | null;
    onToggleGroup: (key: string) => void;
  };
  /** Drawn in the header beside the type filter: the pin view controls. */
  headerControls?: ReactNode;
  /** The pin view's groups, in the Filter menu's Group by under `statusTabs`. */
  pinGrouping?: ReviewListPinGrouping;
  /** "All pins" view: Unresolved opens with the findings to fix, the minor and check ones in a band that folds. */
  severityBands?: boolean;
  /** The findings scroller. The review keeps its scroll here across leaving and reopening. */
  scrollContainerRef?: (node: HTMLElement | null) => void;
  /** To decide / All tabs, high risk first, and Group by (flag `ask-v2-review-status-tabs`). */
  statusTabs?: boolean;
  /** The pin view's Show minor switch, drawn in the Filter menu under `statusTabs`. */
  showMinor?: ReviewListShowMinorControl;
  /** Puts the type, Always-on and raiser boxes back; the Filter menu's Reset. */
  onResetFilter?: () => void;
  statusFilter?: ReviewListStatusFilter;
};

export function AskV2ReviewCommentsList({
  comments,
  companionsOf,
  commentNumbers: commentNumbersProp,
  contextFiles = [],
  reviewFiles = [],
  selectedCommentId: selectedProp = null,
  authorNameByUserId,
  currentUserId,
  currentUserName,
  activeTags = defaultActiveReviewCommentTags(),
  alwaysOn = true,
  showTagFilter = false,
  onToggleTag = () => undefined,
  onToggleAlwaysOn = () => undefined,
  allComments,
  sourceFilter,
  judgmentPending = false,
  onJudge,
  onEdit,
  onDelete,
  onSelect,
  onClear,
  afterUnresolved,
  pinGroups,
  headerControls,
  pinGrouping,
  severityBands = false,
  scrollContainerRef,
  statusTabs = false,
  showMinor,
  onResetFilter,
  statusFilter,
}: AskV2ReviewCommentsListProps) {
  const { t } = useTranslation("ui");
  const sectionIdBase = useId();
  // A companion has no card of its own: its lead's card is the one selected.
  const leadOfCompanion = useMemo(() => {
    const byCompanion = new Map<string, string>();
    companionsOf?.forEach((list, lead) =>
      list.forEach((c) => byCompanion.set(c._id, lead)),
    );
    return byCompanion;
  }, [companionsOf]);
  const selectedCommentId =
    selectedProp == null
      ? null
      : (leadOfCompanion.get(selectedProp) ?? selectedProp);
  const commentNumbers = useMemo(() => {
    if (commentNumbersProp) return commentNumbersProp;
    const numbers: Record<string, number> = {};
    let index = 0;
    for (const comment of comments) {
      if (comment.isDeleted) continue;
      index += 1;
      numbers[comment._id] = index;
    }
    return numbers;
  }, [commentNumbersProp, comments]);

  const { memoryCitationsByComment, claimCitationsByComment } =
    useReviewCommentCitations(comments);

  const authorOptions = {
    aiLabel: t("aiReviewer"),
    currentUserId,
    currentUserName,
    authorNameByUserId,
    unknownAuthorLabel: t("member"),
  };

  // why: stable, or the moved-toast effect re-runs on every render.
  const undoMove = useCallback(
    (commentId: string) => onJudge?.(commentId, null),
    [onJudge],
  );
  const listView = useReviewListView({
    comments,
    selectedCommentId,
    enabled: statusTabs,
    allComments,
    statusFilter,
  });
  const emptyState = reviewListEmptyState({
    activeTags,
    alwaysOn,
    hiddenSourceCount: sourceFilter?.hidden.size ?? 0,
    statusNarrowed: listView.statusNarrowed,
  });
  const {
    sections: allSections,
    sectionSizes,
    unresolvedCount,
    showSections: hasShownComments,
    isSectionOpen,
    toggleSection,
    bandsOn,
    minorBandOpen,
    toggleMinorBand,
  } = useReviewCommentSections({
    comments: listView.shown,
    allComments,
    selectedCommentId,
    severityBands,
    grouped: pinGroups != null,
    statusTabs,
    onUndoMove: statusTabs && onJudge ? undoMove : undefined,
  });
  // why: pin groups keep their sections, or the artwork's group pins would
  // face a flat list with no group to match them.
  const flat = listView.flat && pinGroups == null;
  const showSections = hasShownComments && !flat;
  // To decide emptied by the type filter, not by every comment having a status.
  const showEmptyState =
    comments.length === 0 ||
    (statusTabs && listView.shown.length === 0 && !listView.allDecided);
  const sections = allSections.filter((section) =>
    listView.showsSection(section.key),
  );
  const riskSummary = (items: readonly CommentWithOptionalTags[]) => {
    if (!statusTabs || items.length === 0) return undefined;
    return t("askV2.review.riskSummary", riskCounts(items));
  };
  // The finding whose header delete opened the scope prompt. The prompt
  // renders in that card's open body, so delete on a closed card opens it.
  const [rejectingCommentId, setRejectingCommentId] = useState<string | null>(
    null,
  );
  // why: artwork selection never hits the delete handler; clearing when the selection IS this finding would hide the prompt a closed-card delete just opened
  useEffect(() => {
    setRejectingCommentId((current) =>
      current != null &&
      current !== selectedCommentId &&
      leadOfCompanion.get(current) !== selectedCommentId
        ? null
        : current,
    );
  }, [leadOfCompanion, selectedCommentId]);

  const renderComment = (comment: CommentWithOptionalTags) => (
    <AskV2ReviewCommentCard
      key={comment._id}
      comment={comment}
      companions={companionsOf?.get(comment._id)}
      rejectingId={rejectingCommentId}
      commentNumber={commentNumbers[comment._id] ?? 0}
      expanded={selectedCommentId === comment._id}
      rejecting={rejectingCommentId === comment._id}
      authorOptions={authorOptions}
      claimCitations={claimCitationsByComment.get(comment._id)}
      memoryCitations={memoryCitationsByComment.get(comment._id)}
      contextFiles={contextFiles}
      reviewFiles={reviewFiles}
      authorNameByUserId={authorNameByUserId}
      currentUserId={currentUserId}
      currentUserName={currentUserName}
      judgmentPending={judgmentPending}
      onJudge={onJudge}
      onEdit={onEdit}
      onDelete={onDelete}
      onSelect={onSelect}
      onClear={onClear}
      onRejectingChange={setRejectingCommentId}
      rowActions={statusTabs}
    />
  );

  return (
    <section
      className="flex min-h-0 flex-1 flex-col overflow-hidden"
      data-testid="ask-v2-review-comments"
      aria-label={t("askV2.review.findingsLabel")}
    >
      <AskV2ReviewCommentsHeader
        unresolvedCount={unresolvedCount}
        showSplit={hasShownComments}
        controls={headerControls}
        showTagFilter={showTagFilter}
        activeTags={activeTags}
        alwaysOn={alwaysOn}
        onToggleTag={onToggleTag}
        onToggleAlwaysOn={onToggleAlwaysOn}
        sourceFilter={sourceFilter}
        statusTabs={
          statusTabs
            ? {
                tab: listView.tab,
                counts: listView.counts,
                onChange: listView.setTab,
              }
            : undefined
        }
        groupBy={
          statusTabs
            ? { value: listView.groupBy, onChange: listView.setGroupBy }
            : undefined
        }
        pinGrouping={statusTabs ? pinGrouping : undefined}
        statusFilter={statusTabs ? listView.statusFilter : undefined}
        showMinor={statusTabs ? showMinor : undefined}
        onResetFilter={onResetFilter}
      />
      <ul
        ref={scrollContainerRef}
        className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
        data-testid="ask-v2-review-comment-list"
        aria-label={t("askV2.review.commentsLabel")}
        // why: a memory chip's card keeps inside this list, scrolling yet or not, so a short list never lets it slide over the artwork pane (`popoverBoundary`).
        data-popover-boundary
      >
        {showEmptyState ? (
          <li
            className="px-2 py-6 text-center text-[13px] text-puntt-neutral-gray-10"
            data-testid={emptyState.testId}
          >
            {t(emptyState.copyKey)}
          </li>
        ) : null}
        {listView.allDecided ? (
          <li
            className="flex flex-col items-center gap-2 px-2 py-8 text-center text-[13px] text-puntt-neutral-gray-11"
            data-testid="ask-v2-review-all-decided"
          >
            {/* why: Radix green, as the Confirm button uses; the tailwind green classes render gray in this panel. */}
            <Text as="span" color="green" className="flex">
              <CheckCircle size={28} weight="fill" aria-hidden="true" />
            </Text>
            {t("askV2.review.allDecided")}
          </li>
        ) : null}
        {showSections
          ? sections.map((section) => (
              <Fragment key={section.key}>
                <AskV2ReviewCommentSection
                  sectionKey={section.key}
                  label={t(section.labelKey)}
                  count={section.items.length}
                  riskSummary={riskSummary(section.items)}
                  hiddenCount={Math.max(
                    0,
                    sectionSizes[section.key] - section.items.length,
                  )}
                  open={isSectionOpen(section.key)}
                  listId={`${sectionIdBase}-${section.key}`}
                  onToggle={() => toggleSection(section.key)}
                >
                  {!isSectionOpen(section.key) ? null : section.key ===
                      "unresolved" && pinGroups ? (
                    pinGroups.groups.map((group, index) => {
                      const open = openPinGroup(group);
                      if (!open) return null;
                      return (
                        <AskV2ReviewGroupSection
                          key={group.key}
                          group={open}
                          mark={groupMark(index)}
                          open={pinGroups.openGroupKey === group.key}
                          onToggle={() => pinGroups.onToggleGroup(group.key)}
                        >
                          {open.members.map(renderComment)}
                        </AskV2ReviewGroupSection>
                      );
                    })
                  ) : section.key === "unresolved" && bandsOn ? (
                    <AskV2ReviewSeverityBands
                      items={section.items}
                      minorOpen={minorBandOpen}
                      onToggleMinor={toggleMinorBand}
                      renderComment={renderComment}
                    />
                  ) : (
                    section.items.map(renderComment)
                  )}
                </AskV2ReviewCommentSection>
                {section.key === "unresolved" ? afterUnresolved : null}
              </Fragment>
            ))
          : listView.shown.map(renderComment)}
        {showSections ? null : afterUnresolved}
      </ul>
    </section>
  );
}
